// setColumnCollation — DdlEdit builder + commit dispatcher for the per-column
// collation / character-set surface (DSE2 §3.A4).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice A4.
//
// One op fans into this module:
//   • add-column with `column.collation?: CollationSpec` — the collation spec
//     (name + optional mysql charset) wired into a CREATE TABLE source
//     column-list insert or an ALTER TABLE ADD COLUMN clause. DSE2 §3.A4
//     synthesizers translate to the dialect's idiom (mssql `COLLATE name`,
//     pgsql `COLLATE "name"`, mysql `[CHARACTER SET cs] [COLLATE name]`,
//     oracle `COLLATE name`, sqlite `COLLATE name`).
//
// `alter-column` does not yet carry a `collation` field on `ColumnChange`
// in DSE2 §3.A1's union; consumers wanting to swap a column's collation
// today drop + add (or use the engine's `ALTER COLUMN … COLLATE …` directly
// outside the structural editor). This module exposes a documented predicate
// (`isAlterColumnCollationSupported`) so the form can disable the affordance
// instead of building a doomed edit.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type {
  CollationSpec,
  ColumnDef,
  DdlEdit,
} from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

// ─── add-column with collation ────────────────────────────────────────────

export interface AddColumnWithCollationArgs {
  readonly column: ColumnDef;
  readonly afterColumn?: string;
}

export function buildAddColumnWithCollationEdit(args: AddColumnWithCollationArgs): DdlEdit {
  return {
    op: 'add-column',
    column: args.column,
    afterColumn: args.afterColumn,
  };
}

export async function commitAddColumnWithCollation(
  ctx: VqeDdlEditContext,
  args: AddColumnWithCollationArgs,
): Promise<void> {
  const edit = buildAddColumnWithCollationEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

// ─── per-engine charset gating ────────────────────────────────────────────

/** True when the engine accepts the mysql-only `charset` field on a
 *  `CollationSpec`. The form surfaces a CHARACTER SET input only on mysql;
 *  every other engine renders the input disabled with the documented tooltip. */
export function isCharsetSupported(engine: SqlEngine): boolean {
  return engine === 'mysql';
}

/** Documented tooltip surfaced on the disabled CHARACTER SET input outside
 *  mysql. Mirrors DSE2 §3.A4's per-engine rejection messages so the UI hint
 *  stays in sync with the synthesizer's runtime error. */
export function getCharsetUnsupportedTooltip(engine: SqlEngine): string {
  void engine;
  return 'CHARACTER SET is a MySQL-only column attribute.';
}

// ─── per-engine alter-column gating ───────────────────────────────────────

/** True when the form should enable an `alter-column → set collation` path.
 *  v2 §3.A4 only ships add-column with `CollationSpec`; ColumnChange does not
 *  yet carry a `collation` field. The form consults this predicate (currently
 *  `false` for every engine) and surfaces the documented redirect tooltip
 *  instead of attempting a doomed commit. The `engine` parameter is unused
 *  today — it exists so future widening (per-engine `alter-column → SET
 *  COLLATE` support) can land without changing call sites. */
export function isAlterColumnCollationSupported(engine: SqlEngine): boolean {
  void engine;
  return false;
}

/** Documented redirect tooltip for the alter-column collation path. Points
 *  consumers at the drop + add workaround that works today across every
 *  supported engine. */
export function getAlterColumnCollationUnsupportedTooltip(engine: SqlEngine): string {
  void engine;
  return (
    'Column collation cannot be modified in place from this surface yet. '
      + 'Drop the column and add it back with the new collation.'
  );
}

// ─── per-engine version notes ─────────────────────────────────────────────

/** Engine-version requirement note (best-effort, not a runtime version probe).
 *  Oracle column-level collation requires 12.2+ and `MAX_STRING_SIZE = EXTENDED`.
 *  The form surfaces this string as a hint when the engine is oracle. Other
 *  engines return an empty string. */
export function getCollationEngineVersionNote(engine: SqlEngine): string {
  if (engine === 'oracle') {
    return (
      'Column collation requires Oracle 12.2+ with MAX_STRING_SIZE = EXTENDED.'
    );
  }
  return '';
}

// ─── per-engine validation for CollationSpec on add-column ────────────────

/** Pre-emit validation mirroring DSE2's per-engine `formatCollationSpec` rules.
 *  Returns null when the spec is acceptable for the engine; returns a human
 *  message when the synthesizer would throw. The form uses this to disable
 *  Submit before the user attempts a doomed commit. */
export function validateCollationSpecForEngine(
  engine: SqlEngine,
  spec: CollationSpec,
): string | null {
  const hasName = spec.name !== undefined && spec.name.trim() !== '';
  const hasCharset = spec.charset !== undefined && spec.charset.trim() !== '';
  if (!hasName && !hasCharset) {
    return "Collation requires a name (or 'charset' on MySQL).";
  }
  if (engine !== 'mysql' && hasCharset) {
    return `${engine} does not accept 'charset' on a column collation — CHARACTER SET is a MySQL-only attribute.`;
  }
  if (engine !== 'mysql' && !hasName) {
    return `${engine} collation requires a name — only MySQL accepts a bare CHARACTER SET.`;
  }
  return null;
}

/** True when the supplied `CollationSpec` is non-empty — i.e. at least one of
 *  `name` or `charset` is provided. The bare-spec `{}` shape is treated as
 *  "user has not chosen anything yet" and the form omits the field on submit. */
export function isNonEmptyCollationSpec(spec: CollationSpec): boolean {
  const hasName = spec.name !== undefined && spec.name.trim() !== '';
  const hasCharset = spec.charset !== undefined && spec.charset.trim() !== '';
  return hasName || hasCharset;
}

/** Build a clean `CollationSpec` from form fields, dropping empty strings. The
 *  form keeps both fields as bare strings so the inputs are controlled; this
 *  helper trims + filters before submit so `{ name: '' }` doesn't reach the
 *  synthesizer (DSE2's emitter accepts any non-empty `name` and would emit the
 *  empty string verbatim, producing invalid SQL). */
export function buildCollationSpecFromForm(
  name: string,
  charset: string,
): CollationSpec {
  const trimmedName = name.trim();
  const trimmedCharset = charset.trim();
  const spec: { name?: string; charset?: string } = {};
  if (trimmedName !== '') spec.name = trimmedName;
  if (trimmedCharset !== '') spec.charset = trimmedCharset;
  return spec as CollationSpec;
}
